import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { ErrorMessage, PageLoader } from '@/components/ui'
import { api } from '@/lib/api'
import { formatCellValue, formatDateTime, formatNumber } from '@/lib/format'
import {
  COLUMN_TYPE_TITLES,
  type ColumnProfile,
  type DatasetVersionDetail,
  type IssueSeverity,
  type QualityIssue,
} from '@/lib/types'
import { useActiveWorkspace } from '@/stores/auth'

/**
 * Экран результата импорта.
 *
 * Главная задача — показать, что система поняла в файле, и что с данными не
 * так. Это не технический лог: пользователь должен увидеть свои колонки под
 * своими названиями, узнать, какие строки исключены и в каких строках ошибки,
 * и понять, можно ли строить отчёты.
 */
export function DatasetVersionPage() {
  const { versionId } = useParams<{ versionId: string }>()
  const workspace = useActiveWorkspace()

  const version = useQuery({
    queryKey: ['dataset-version', versionId],
    queryFn: () => api.get<DatasetVersionDetail>(`/dataset-versions/${versionId}`),
    enabled: Boolean(versionId) && Boolean(workspace),
  })

  if (!workspace || version.isLoading) return <PageLoader label="Загружаем профиль данных…" />

  if (version.error) {
    return (
      <div className="space-y-4">
        <ErrorMessage error={version.error} />
        <Link to="/sources" className="btn-secondary">
          Вернуться к источникам
        </Link>
      </div>
    )
  }

  const data = version.data
  if (!data) return null

  const errors = data.issues.filter((issue) => issue.severity === 'error')
  const warnings = data.issues.filter((issue) => issue.severity === 'warning')
  const infos = data.issues.filter((issue) => issue.severity === 'info')

  return (
    <div className="space-y-6">
      <section className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500">Версия {data.version_no}</p>
            <h2 className="truncate text-lg font-semibold text-slate-900">{data.file_name}</h2>
            <p className="tabular mt-1 text-sm text-slate-600">
              {formatNumber(data.row_count)} строк · {data.column_count} колонок
              {data.sheet_name ? ` · лист «${data.sheet_name}»` : ''}
              {data.encoding ? ` · кодировка ${data.encoding}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Link
              to={`/dataset-versions/${data.id}/mapping`}
              className={data.mapping_confirmed_at ? 'btn-secondary' : 'btn-primary'}
            >
              {data.mapping_confirmed_at ? 'Изменить сопоставление' : 'Указать смысл колонок'}
            </Link>
          </div>
        </div>

        <dl className="mt-5 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {data.header_row !== null && (
            <Row label="Заголовки найдены в строке" value={String(data.header_row)} />
          )}
          {data.skipped_service_rows > 0 && (
            <Row
              label="Исключено итоговых строк"
              value={String(data.skipped_service_rows)}
              hint="иначе суммы удвоились бы"
            />
          )}
          <Row label="Загружено" value={formatDateTime(data.created_at)} />
          <Row
            label="Сопоставление колонок"
            value={
              data.mapping_confirmed_at
                ? `подтверждено ${formatDateTime(data.mapping_confirmed_at)}`
                : 'не подтверждено'
            }
          />
        </dl>

        {!data.mapping_confirmed_at && (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Чтобы строить отчёты, укажите смысл колонок: где дата, где выручка, где клиент. Система
            уже предложила варианты — их нужно проверить и подтвердить.
          </p>
        )}
      </section>

      {data.issues.length > 0 && (
        <section className="card p-6">
          <h3 className="text-base font-semibold text-slate-900">
            Что нужно знать о данных
            <span className="ml-2 text-sm font-normal text-slate-500">
              {errors.length > 0 && `ошибок: ${errors.length}`}
              {errors.length > 0 && warnings.length > 0 && ', '}
              {warnings.length > 0 && `предупреждений: ${warnings.length}`}
            </span>
          </h3>

          <ul className="mt-4 space-y-2.5">
            {[...errors, ...warnings, ...infos].map((issue, index) => (
              <IssueRow key={`${issue.code}-${index}`} issue={issue} columns={data.columns} />
            ))}
          </ul>
        </section>
      )}

      <section className="card overflow-hidden">
        <header className="border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-semibold text-slate-900">Колонки файла</h3>
          <p className="mt-1 text-xs text-slate-500">
            Типы определены по значениям, а не по заголовкам: русские числа и даты распознаются
            автоматически.
          </p>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-6 py-2.5 font-medium">Колонка</th>
                <th className="px-3 py-2.5 font-medium">Тип</th>
                <th className="px-3 py-2.5 font-medium">Заполнено</th>
                <th className="px-3 py-2.5 font-medium">Уникальных</th>
                <th className="px-3 py-2.5 font-medium">Диапазон</th>
                <th className="px-6 py-2.5 font-medium">Примеры</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.columns.map((column) => (
                <ColumnRow key={column.column_index} column={column} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {data.preview.length > 0 && (
        <section className="card overflow-hidden">
          <header className="border-b border-slate-100 px-6 py-4">
            <h3 className="text-base font-semibold text-slate-900">Предпросмотр данных</h3>
            <p className="mt-1 text-xs text-slate-500">
              Первые {data.preview.length} строк после приведения значений к типам
            </p>
          </header>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  {data.columns.map((column) => (
                    <th
                      key={column.column_index}
                      className="whitespace-nowrap px-3 py-2 font-medium"
                    >
                      {column.source_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.preview.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {data.columns.map((column) => {
                      const value = row[column.normalized_name]
                      return (
                        <td
                          key={column.column_index}
                          className={`whitespace-nowrap px-3 py-2 ${
                            value === null || value === undefined
                              ? 'text-slate-300'
                              : 'text-slate-700'
                          } ${isNumericType(column) ? 'tabular text-right' : ''}`}
                        >
                          {formatCellValue(value, column.detected_type)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Link to="/sources" className="btn-ghost">
        ← Все источники данных
      </Link>
    </div>
  )
}

function isNumericType(column: ColumnProfile): boolean {
  return column.detected_type === 'integer' || column.detected_type === 'decimal'
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-900">
        {value}
        {hint && <span className="ml-1 text-xs text-slate-400">({hint})</span>}
      </dd>
    </div>
  )
}

function IssueRow({ issue, columns }: { issue: QualityIssue; columns: ColumnProfile[] }) {
  const styles: Record<IssueSeverity, string> = {
    error: 'border-red-200 bg-red-50 text-red-900',
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
    info: 'border-slate-200 bg-slate-50 text-slate-700',
  }
  const labels: Record<IssueSeverity, string> = {
    error: 'Ошибка',
    warning: 'Внимание',
    info: 'К сведению',
  }

  const column =
    issue.column_index !== null
      ? columns.find((item) => item.column_index === issue.column_index)
      : undefined

  // Номера строк — то, что позволяет пользователю проверить решение системы
  // в своём файле. Без них сообщение бесполезно.
  const rowNumbers = Array.isArray(issue.details.row_numbers)
    ? (issue.details.row_numbers as number[])
    : []

  return (
    <li className={`rounded-lg border p-3 text-sm ${styles[issue.severity]}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-70">
          {labels[issue.severity]}
        </span>
        {column && <span className="text-xs opacity-70">колонка «{column.source_name}»</span>}
      </div>
      <p className="mt-1">{issue.message}</p>
      {rowNumbers.length > 8 && (
        <p className="mt-1 text-xs opacity-70">
          Всего затронуто строк: {formatNumber(issue.affected_rows)}
        </p>
      )}
    </li>
  )
}

function ColumnRow({ column }: { column: ColumnProfile }) {
  const filledPercent = Math.round((1 - column.null_ratio) * 100)

  return (
    <tr>
      <td className="px-6 py-2.5">
        <span className="font-medium text-slate-900">{column.source_name}</span>
        {column.is_candidate_key && (
          <span className="badge ml-2 bg-brand-100 text-[10px] text-brand-800">ключ</span>
        )}
        {column.contains_percent && (
          <span className="badge ml-2 bg-violet-100 text-[10px] text-violet-800">проценты</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-slate-600">{COLUMN_TYPE_TITLES[column.detected_type]}</td>
      <td className="tabular px-3 py-2.5">
        <span className={filledPercent < 50 ? 'text-amber-700' : 'text-slate-600'}>
          {filledPercent}%
        </span>
        {column.unparsed_count > 0 && (
          <span className="ml-1 text-xs text-amber-700">
            (не прочитано: {column.unparsed_count})
          </span>
        )}
      </td>
      <td className="tabular px-3 py-2.5 text-slate-600">{formatNumber(column.distinct_count)}</td>
      <td className="tabular px-3 py-2.5 text-xs text-slate-500">
        {column.min_value !== null
          ? `${formatCellValue(column.min_value, column.detected_type)} … ${formatCellValue(
              column.max_value,
              column.detected_type,
            )}`
          : '—'}
      </td>
      <td className="max-w-xs truncate px-6 py-2.5 text-xs text-slate-500">
        {column.sample_values.slice(0, 3).join(', ') || '—'}
      </td>
    </tr>
  )
}
